﻿import { useBookDemo } from './BookDemoContext'
import './sections.css'

const LinkedInSVG = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
)

export default function AboutSection() {
  const openBookDemo = useBookDemo()
  return (
    <section id="about" className="section about-section">
      <div className="container">
        <h2 className="section-title">Built by People Who Lived the Problem</h2>
        <p className="section-subtitle">PossoBuild was founded by two Microsoft leaders who spent years watching enterprises struggle with disconnected HR systems and inefficient hiring.</p>

        <div className="about-content">
          <div className="about-text">
            <h3>The Problem We Lived at Microsoft</h3>
            <p>For years, we led large-scale initiatives at Microsoft. Kalyan ran the Services Practice for Data &amp; AI, working with enterprises implementing technology at scale. Uma led engineering teams, hiring and developing hundreds of engineers.</p>
            <p>Our biggest frustration wasn't the technology — it was the people systems. Every week, engineering teams burned 6–8 hours per hire on unqualified candidates. Talented employees asked for career development tools but got generic training catalogs. The HR tech stack cost millions but delivered no intelligence.</p>
            <p>We saw the same pattern across every enterprise we worked with:</p>
            <ul className="about-arrow-list">
              <li className="about-arrow-item"><span className="about-arrow-icon">→</span><span>Hiring decisions rely on resumes, not real capability</span></li>
              <li className="about-arrow-item"><span className="about-arrow-icon">→</span><span>Employee skills remain invisible until someone leaves</span></li>
              <li className="about-arrow-item"><span className="about-arrow-icon">→</span><span>Career development lacks direction or measurable outcomes</span></li>
              <li className="about-arrow-item"><span className="about-arrow-icon">→</span><span>Employees waste hours searching for policies and information they need now</span></li>
            </ul>
            <h3>The Solution We Built</h3>
            <p>We left Microsoft to fix it. PossoBuild connects hiring, skill assessment, career development, and enterprise knowledge into one unified AI platform — generating actionable workforce intelligence across the entire employee lifecycle.</p>
            <p className="about-blockquote">"Our vision is to be the AI intelligence layer for every workforce decision — helping organisations hire better, develop talent faster, and unlock the potential already inside their teams."</p>
          </div>
        </div>

        <div className="founders-section">
          {/* Kalyan — photo left, text right */}
          <div className="founder-card">
            <div className="founder-photo-side">
              <div className="founder-photo-container">
                <img
                  src="kalyan.jpeg"
                  alt="Kalyan Jata"
                  className="founder-photo"
                  onError={e => { const t = e.target as HTMLImageElement; t.style.display = 'none'; (t.nextElementSibling as HTMLElement).style.display = 'flex'; }}
                />
                <div className="founder-photo-initials" style={{ background: 'linear-gradient(135deg,#1c2b3a,#c9a84c)' }}>KJ</div>
                <a href="https://www.linkedin.com/in/kalyan-jata?utm_source=share_via&utm_content=profile&utm_medium=member_android" target="_blank" rel="noopener noreferrer" className="founder-badge">
                  <LinkedInSVG size={13} />
                </a>
              </div>
            </div>
            <div className="founder-body">
              <h4>Kalyan Jata</h4>
              <p className="founder-role-tag">Co-Founder</p>
              <p className="founder-position">ex-Microsoft · Services Practice Leader, Data &amp; AI</p>
              <p className="founder-bio">Led enterprise AI implementations at scale across Fortune 500 companies. Saw firsthand how disconnected HR tools cost organisations millions in wasted hiring and talent potential.</p>
              <a href="https://www.linkedin.com/in/kalyan-jata?utm_source=share_via&utm_content=profile&utm_medium=member_android" target="_blank" rel="noopener noreferrer" className="founder-ln-btn">
                <LinkedInSVG />
                Connect on LinkedIn
              </a>
            </div>
          </div>

          {/* Uma — text left, photo right */}
          <div className="founder-card founder-card--photo-right">
            <div className="founder-body">
              <h4>Uma Nelluri</h4>
              <p className="founder-role-tag">Co-Founder</p>
              <p className="founder-position">ex-Microsoft · Engineering Leader</p>
              <p className="founder-bio">Hired and developed 200+ engineers at Microsoft. Watched talented people leave for lack of clear career paths. Built the systems to fix both — now powering PossoBuild's talent intelligence platform.</p>
              <a href="https://www.linkedin.com/in/unelluri?utm_source=share_via&utm_content=profile&utm_medium=member_android" target="_blank" rel="noopener noreferrer" className="founder-ln-btn">
                <LinkedInSVG />
                Connect on LinkedIn
              </a>
            </div>
            <div className="founder-photo-side">
              <div className="founder-photo-container">
                <img
                  src="uma.jpeg"
                  alt="Uma Nelluri"
                  className="founder-photo"
                  onError={e => { const t = e.target as HTMLImageElement; t.style.display = 'none'; (t.nextElementSibling as HTMLElement).style.display = 'flex'; }}
                />
                <div className="founder-photo-initials" style={{ background: 'linear-gradient(135deg,#c9a84c,#1c2b3a)' }}>UN</div>
                <a href="https://www.linkedin.com/in/unelluri?utm_source=share_via&utm_content=profile&utm_medium=member_android" target="_blank" rel="noopener noreferrer" className="founder-badge">
                  <LinkedInSVG size={13} />
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="founder-credentials">
          <h4>Why This Matters</h4>
          <ul>
            <li>✓ Built by leaders who understand enterprise scale—not first-time entrepreneurs guessing</li>
            <li>✓ Solves real problems we lived for years—not hypothetical opportunities</li>
            <li>✓ Combines enterprise AI expertise with deep talent management experience</li>
            <li>✓ Technical founders who can actually build what's promised</li>
          </ul>
        </div>

        <div className="enterprise-proof">
          <h3>Enterprise-Proven</h3>
          <p style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.6)', marginBottom: '1rem' }}>Designed for organisations where workforce decisions affect thousands of employees and millions in talent investment.</p>
          <p>PossoBuild powers talent intelligence for:</p>
          <ul>
            <li>✓ Global professional services firms (Big 4 consulting)</li>
            <li>✓ Fortune 100 IT services companies</li>
            <li>✓ Enterprise learning platforms serving 100,000+ users</li>
            <li>✓ Proprietary CRM systems serving thousands of businesses</li>
          </ul>
          <p><em>Our technology meets the security, scale, and reliability standards required by the world's most demanding enterprises.</em></p>
          <div className="enterprise-cta-box">
            <p>Ready to transform your workforce intelligence?</p>
            <button
              className="btn-primary btn-large"
              onClick={() => openBookDemo('Request a Demo')}
            >
              Request a Demo →
            </button>
          </div>
        </div>

        <div className="trust-indicators-bottom">
          <div className="trust-item">
            <div className="trust-icon">🏢</div>
            <div className="trust-label">Built on Microsoft Azure</div>
          </div>
          <div className="trust-item">
            <div className="trust-icon">🔒</div>
            <div className="trust-label">Enterprise Security</div>
          </div>
          <div className="trust-item">
            <div className="trust-icon">⚡</div>
            <div className="trust-label">99.9% Uptime SLA</div>
          </div>
          <div className="trust-item">
            <div className="trust-icon">🎯</div>
            <div className="trust-label">Big 4 &amp; Fortune 100 Trusted</div>
          </div>
        </div>
      </div>
    </section>
  )
}
